import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, Volume2, VolumeX, ArrowRight, Camera, Film, Instagram, Youtube, Globe, CheckCircle2, Loader2, ChevronRight, Mail, ExternalLink, Star, Quote } from 'lucide-react';
import { PageSEO } from '../components/seo/SchemaMarkup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inject JSON-LD schema
const injectJsonLd = (id, data) => {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
  return script;
};

const HYROX_CONTENT_FAQ = [
  { q: 'What types of HYROX content do you create?', a: 'We produce cinematic race recaps, pre-race hype reels, full-length event films, athlete profiles, gym & brand content, and social media packages optimized for Instagram, TikTok, and YouTube.' },
  { q: 'How much does HYROX video production cost?', a: 'All packages are custom-quoted based on your needs — number of athletes, event location, deliverables, and turnaround time. Contact us for a free quote.' },
  { q: 'Do you film at all HYROX events in the US?', a: 'Yes, our team travels to HYROX events across the United States. We have filmed at events in Dallas, Chicago, Houston, Los Angeles, and many more cities.' },
  { q: 'How long does it take to receive my finished HYROX video?', a: 'Turnaround depends on the package. Hype reels and short-form content are typically delivered within 5-7 business days. Full event films and athlete profiles may take 2-3 weeks.' },
  { q: 'Can I use the videos on my social media?', a: 'Absolutely! All deliverables are yours to use across social media, websites, and marketing materials. We optimize for platform-specific formats (9:16, 16:9, 1:1).' },
  { q: 'Do you offer ongoing content packages for gyms?', a: 'Yes, we offer monthly retainer packages for gyms and coaches that include regular short-form video production, typically 3-5 videos per session, optimized for all social platforms.' },
];

const VIDEOS = [
  {
    id: 'hyrox-recap',
    title: 'HYROX Race Recap',
    category: 'Race Recap',
    desc: 'Cinematic race day highlights',
    src: 'https://customer-assets.emergentagent.com/job_a976fb05-21a8-471d-88e2-aad0bf8a8eeb/artifacts/kumi84ge_HYROX%20RECAP.mp4',
    poster: '/posters/hyrox-recap.jpg',
  },
  {
    id: 'station-skierg',
    title: 'SkiErg Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic SkiErg technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/heww5odd_Daniel%20and%20Elli%20Individual%20Clips00415387.mov',
    poster: '/posters/skierg.jpg',
  },
  {
    id: 'station-sled-push',
    title: 'Sled Push Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic sled push technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/jn8rd8ib_Daniel%20and%20Elli%20Individual%20Clips00421357.mov',
    poster: '/posters/sled-push.jpg',
  },
  {
    id: 'station-sled-pull',
    title: 'Sled Pull Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic sled pull technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/jc2dfn6a_Daniel%20and%20Elli%20Individual%20Clips00424143.mov',
    poster: '/posters/sled-pull.jpg',
  },
  {
    id: 'station-burpee-broad-jump',
    title: 'Burpee Broad Jump Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic burpee broad jump technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/eoeovu73_Daniel%20and%20Elli%20Individual%20Clips00427672.mov',
    poster: '/posters/burpee-broad-jump.jpg',
  },
  {
    id: 'station-rower',
    title: 'Rowing Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic rowing technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/g910lzp9_Daniel%20and%20Elli%20Individual%20Clips00433345.mov',
    poster: '/posters/rower.jpg',
  },
  {
    id: 'station-farmers-carry',
    title: 'Farmers Carry Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic farmers carry technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/d507xqjq_Daniel%20and%20Elli%20Individual%20Clips00435530.mov',
    poster: '/posters/farmers-carry.jpg',
  },
  {
    id: 'station-sandbag-lunges',
    title: 'Sandbag Lunges Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic sandbag lunges technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/mwq1t9or_Daniel%20and%20Elli%20Individual%20Clips00438894.mov',
    poster: '/posters/sandbag-lunges.jpg',
  },
  {
    id: 'station-wall-balls',
    title: 'Wall Balls Station Film',
    category: 'Station Mastery',
    desc: 'Cinematic wall balls technique & race footage',
    src: 'https://customer-assets.emergentagent.com/job_c7785eb2-bf9f-4901-baad-250c11530aa2/artifacts/y6y9g1i6_Daniel%20and%20Elli%20Individual%20Clips00441135.mov',
    poster: '/posters/wall-balls.jpg',
  },
  {
    id: 'hype-1',
    title: 'Athlete Hype Video',
    category: 'Hype Reel',
    desc: 'Cinematic athlete hype content',
    src: '/videos/hype-1.mp4',
    poster: '/posters/hype-1.jpg',
  },
  {
    id: 'fnl-hype',
    title: 'FNL Hype Reel',
    category: 'Hype Reel',
    desc: 'High-energy pre-race content',
    src: '/videos/fnl-hype.mp4',
    poster: '/posters/fnl-hype.jpg',
  },
  {
    id: 'grnd-recap',
    title: 'GRND x Downtown Recap',
    category: 'Event Film',
    desc: 'Full event coverage & highlights',
    src: '/videos/grnd-recap.mp4',
    poster: '/posters/grnd-recap.jpg',
  },
  {
    id: 'hollywood-reel',
    title: 'Hollywood Showreel',
    category: 'Showreel',
    desc: 'Cinematic production showcase',
    src: '/videos/hollywood-reel.mp4',
    poster: '/posters/hollywood-reel.jpg',
  },
];

const SERVICES = [
  { title: 'Race Day Recaps', desc: 'Cinematic highlights of your HYROX race — every station, every push, every finish line moment.', icon: Film },
  { title: 'Hype Reels', desc: 'High-energy short-form content designed for Instagram Reels, TikTok, and YouTube Shorts.', icon: Play },
  { title: 'Event Films', desc: 'Full-length event coverage — gym openings, training camps, community races, and branded events.', icon: Camera },
  { title: 'Athlete Profiles', desc: 'Personal story-driven films that showcase an athlete\'s journey, training, and race day.', icon: CheckCircle2 },
  { title: 'Gym & Brand Content', desc: 'Monthly content packages for gyms, coaches, and fitness brands building their HYROX presence.', icon: Instagram },
  { title: 'Social Media Packages', desc: 'Ongoing short-form video production — 3-5 videos per session, optimized for all platforms.', icon: Globe },
];

const VIDEO_TYPE_OPTIONS = [
  'Race Day Recap',
  'Hype Reel',
  'Event Film',
  'Athlete Profile',
  'Gym Content',
  'Social Media Package',
  'Other',
];

function VideoCard({ video }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [canPlay, setCanPlay] = useState(false);

  const tryPlay = () => {
    if (!videoRef.current) return;
    setLoading(true);
    const playPromise = videoRef.current.play();
    if (playPromise) {
      playPromise.then(() => {
        setPlaying(true);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
  };

  const handleHoverIn = () => {
    if (expanded) return;
    tryPlay();
  };

  const handleHoverOut = () => {
    if (expanded || !videoRef.current) return;
    videoRef.current.pause();
    setPlaying(false);
  };

  const handleClick = () => {
    if (expanded) {
      // Toggle play/pause in expanded mode
      if (!videoRef.current) return;
      if (playing) {
        videoRef.current.pause();
        setPlaying(false);
      } else {
        tryPlay();
      }
    } else {
      // Expand the card and start playing with controls
      setExpanded(true);
      setMuted(false);
      if (videoRef.current) {
        videoRef.current.muted = false;
      }
      tryPlay();
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setExpanded(false);
    setMuted(true);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.muted = true;
      videoRef.current.currentTime = 0;
    }
    setPlaying(false);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  // Expanded full-screen modal
  if (expanded) {
    return (
      <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={handleClose} data-testid={`video-expanded-${video.id}`}>
        <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
          <video
            ref={videoRef}
            src={video.src}
            poster={video.poster}
            muted={muted}
            loop
            playsInline
            controls
            autoPlay
            className="w-full rounded"
            data-testid={`video-player-${video.id}`}
          />
          <button
            onClick={handleClose}
            className="absolute -top-10 right-0 text-zinc-400 hover:text-white text-sm font-heading uppercase tracking-wider flex items-center gap-2 transition-colors"
            data-testid={`video-close-${video.id}`}
          >
            Close ✕
          </button>
          <div className="mt-3">
            <span className="text-[#e8ff00] text-[10px] uppercase tracking-wider font-heading">{video.category}</span>
            <p className="text-white text-lg font-heading mt-1">{video.title}</p>
            <p className="text-zinc-500 text-sm">{video.desc}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group relative aspect-[9/16] bg-zinc-900 border border-zinc-800 overflow-hidden cursor-pointer hover:border-[#e8ff00]/40 transition-all"
      onMouseEnter={handleHoverIn}
      onMouseLeave={handleHoverOut}
      onClick={handleClick}
      data-testid={`video-${video.id}`}
    >
      <video
        ref={videoRef}
        src={video.src}
        poster={video.poster}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
        onCanPlay={() => setCanPlay(true)}
      />
      {/* Overlay — always visible when not playing, shows on hover when playing */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent transition-opacity duration-300 ${playing && !loading ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
        <div className="absolute top-3 left-3">
          <span className="px-2 py-0.5 bg-[#e8ff00] text-black text-[9px] font-heading tracking-widest uppercase">{video.category}</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-sm font-heading">{video.title}</p>
          <p className="text-zinc-500 text-[11px] mt-0.5">{video.desc}</p>
        </div>
        {/* Central play button */}
        {(!playing || loading) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-[#e8ff00]/20 backdrop-blur-sm border border-[#e8ff00]/40 flex items-center justify-center group-hover:bg-[#e8ff00]/30 group-hover:scale-110 transition-all duration-300">
              {loading ? (
                <Loader2 className="w-6 h-6 text-[#e8ff00] animate-spin" />
              ) : (
                <Play className="w-6 h-6 text-[#e8ff00] ml-0.5" fill="currentColor" />
              )}
            </div>
          </div>
        )}
      </div>
      {/* Mute toggle when playing */}
      {playing && !loading && (
        <button
          onClick={toggleMute}
          className="absolute top-3 right-3 w-8 h-8 bg-black/60 backdrop-blur-sm border border-zinc-700 flex items-center justify-center hover:border-[#e8ff00]/40 transition-colors z-10"
          data-testid={`mute-${video.id}`}
        >
          {muted ? <VolumeX className="w-3.5 h-3.5 text-zinc-400" /> : <Volume2 className="w-3.5 h-3.5 text-[#e8ff00]" />}
        </button>
      )}
    </div>
  );
}

function InquiryForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', instagram: '', event_name: '', event_date: '', event_location: '', video_types: [], message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const toggleVideoType = (type) => {
    setForm(prev => ({
      ...prev,
      video_types: prev.video_types.includes(type)
        ? prev.video_types.filter(t => t !== type)
        : [...prev.video_types, type],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/content-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!resp.ok) throw new Error('Failed to submit');
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-16" data-testid="inquiry-success">
        <CheckCircle2 className="w-12 h-12 text-[#e8ff00] mx-auto mb-4" />
        <h3 className="text-white text-2xl font-heading mb-2">INQUIRY RECEIVED</h3>
        <p className="text-zinc-400 text-sm max-w-md mx-auto">We'll get back to you within 24-48 hours to discuss your project. Check your email for a confirmation.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="content-inquiry-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Name *</label>
          <input type="text" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
            placeholder="Your full name" data-testid="inquiry-name" />
        </div>
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Email *</label>
          <input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
            placeholder="your@email.com" data-testid="inquiry-email" />
        </div>
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Phone</label>
          <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
            placeholder="(555) 555-5555" data-testid="inquiry-phone" />
        </div>
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Instagram</label>
          <input type="text" value={form.instagram} onChange={e => setForm(p => ({ ...p, instagram: e.target.value }))}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
            placeholder="@yourhandle" data-testid="inquiry-instagram" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Event Name</label>
          <input type="text" value={form.event_name} onChange={e => setForm(p => ({ ...p, event_name: e.target.value }))}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
            placeholder="e.g. HYROX Dallas 2026" data-testid="inquiry-event-name" />
        </div>
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Event Date</label>
          <input type="date" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
            data-testid="inquiry-event-date" />
        </div>
        <div>
          <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Location</label>
          <input type="text" value={form.event_location} onChange={e => setForm(p => ({ ...p, event_location: e.target.value }))}
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors"
            placeholder="City, State" data-testid="inquiry-event-location" />
        </div>
      </div>

      <div>
        <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-2">What type of content are you looking for?</label>
        <div className="flex flex-wrap gap-2" data-testid="inquiry-video-types">
          {VIDEO_TYPE_OPTIONS.map(type => (
            <button key={type} type="button" onClick={() => toggleVideoType(type)}
              className={`px-3 py-1.5 text-xs border transition-all ${
                form.video_types.includes(type)
                  ? 'border-[#e8ff00] bg-[#e8ff00]/10 text-[#e8ff00]'
                  : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
              }`}
              data-testid={`type-${type.toLowerCase().replace(/\s/g, '-')}`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading block mb-1.5">Tell us about your project</label>
        <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
          className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors min-h-[120px] resize-none"
          placeholder="Describe what you're looking for — number of athletes, style preferences, deliverable format, timeline, etc."
          data-testid="inquiry-message" />
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <button type="submit" disabled={submitting}
        className="w-full md:w-auto px-8 py-3.5 bg-[#e8ff00] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="inquiry-submit-btn"
      >
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Send Inquiry <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

const REVIEW_SERVICE_OPTIONS = ['Race Day Recap', 'Hype Reel', 'Event Film', 'Athlete Profile', 'Gym Content', 'Social Media Package'];

function ReviewSubmitForm({ onSubmitted }) {
  const [form, setForm] = useState({ name: '', role: '', location: '', rating: 5, quote: '', service_type: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.quote) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/api/content-reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (resp.ok) {
        setSubmitted(true);
        if (onSubmitted) onSubmitted();
      }
    } catch (e) { /* silent */ }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="review-submit-success">
        <CheckCircle2 className="w-8 h-8 text-[#e8ff00] mx-auto mb-3" />
        <p className="text-white font-heading text-sm">REVIEW SUBMITTED</p>
        <p className="text-zinc-500 text-xs mt-1">Your review will appear once approved. Thank you!</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-submit-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input type="text" required placeholder="Your name *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          className="px-3 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors" data-testid="review-name" />
        <input type="text" placeholder="Role (e.g. HYROX Athlete)" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
          className="px-3 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors" data-testid="review-role" />
        <input type="text" placeholder="Location (e.g. Dallas, TX)" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
          className="px-3 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none transition-colors" data-testid="review-location" />
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-zinc-500 text-xs mr-1">Rating:</span>
          {[1,2,3,4,5].map(s => (
            <button key={s} type="button" onClick={() => setForm(p => ({...p, rating: s}))} data-testid={`review-star-${s}`}>
              <Star className={`w-5 h-5 transition-colors ${s <= form.rating ? 'text-[#e8ff00] fill-[#e8ff00]' : 'text-zinc-700'}`} />
            </button>
          ))}
        </div>
        <select value={form.service_type} onChange={e => setForm(p => ({ ...p, service_type: e.target.value }))}
          className="px-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none" data-testid="review-service-type">
          <option value="">Service type (optional)</option>
          {REVIEW_SERVICE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <textarea required placeholder="Share your experience... *" value={form.quote} onChange={e => setForm(p => ({ ...p, quote: e.target.value }))}
        className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none min-h-[80px] resize-none transition-colors" data-testid="review-quote" />
      <button type="submit" disabled={submitting}
        className="px-6 py-2.5 bg-[#e8ff00] text-black font-heading text-xs uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2"
        data-testid="review-submit-btn">
        {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Submitting...</> : 'Submit Review'}
      </button>
    </form>
  );
}

function TestimonialsSection() {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const fetchReviews = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/content-reviews`);
      if (resp.ok) setReviews(await resp.json());
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchReviews(); }, []);

  const featured = reviews.find(r => r.is_featured) || reviews[0];
  const grid = reviews.filter(r => r.review_id !== (featured?.review_id));

  return (
    <section className="bg-black section-spacing" data-testid="content-testimonials">
      <div className="container-ninja">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3 flex items-center justify-center gap-2">
              <Star className="w-4 h-4" /> What They Say
            </p>
            <h2 className="text-3xl md:text-4xl text-white font-heading">ATHLETE & GYM REVIEWS</h2>
          </div>

          {/* Featured testimonial */}
          {featured && (
            <div className="border border-[#e8ff00]/20 bg-[#e8ff00]/5 p-8 md:p-10 mb-6 relative" data-testid="testimonial-featured">
              <Quote className="absolute top-6 right-6 w-10 h-10 text-[#e8ff00]/10" />
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: featured.rating || 5 }, (_, k) => `s-${featured.id || 'f'}-${k}`).map(k => (
                  <Star key={k} className="w-4 h-4 text-[#e8ff00] fill-[#e8ff00]" />
                ))}
              </div>
              <p className="text-white text-lg md:text-xl leading-relaxed mb-6 max-w-2xl">
                "{featured.quote}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#e8ff00]/20 border border-[#e8ff00]/30 flex items-center justify-center font-heading text-[#e8ff00] text-sm">
                  {featured.initials}
                </div>
                <div>
                  <p className="text-white text-sm font-heading">{featured.name?.toUpperCase()}</p>
                  <p className="text-zinc-500 text-xs">{featured.role}{featured.location ? ` \u2022 ${featured.location}` : ''}</p>
                </div>
              </div>
            </div>
          )}

          {/* Grid testimonials */}
          {grid.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {grid.slice(0, 6).map((t, i) => (
                <div key={t.review_id} className="border border-zinc-800 bg-zinc-900/40 p-6 flex flex-col" data-testid={`testimonial-${i}`}>
                  <div className="flex items-center gap-0.5 mb-3">
                    {[...Array(t.rating || 5)].map((__ksi) => (
                      <Star key={`s-${__ksi}`} className="w-3 h-3 text-[#e8ff00] fill-[#e8ff00]" />
                    ))}
                    {t.service_type && (
                      <span className="ml-auto text-zinc-600 text-[9px] uppercase tracking-wider">{t.service_type}</span>
                    )}
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed flex-1 mb-4">"{t.quote}"</p>
                  <div className="flex items-center gap-2.5 pt-3 border-t border-zinc-800/50">
                    <div className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-[10px] font-heading">
                      {t.initials}
                    </div>
                    <div>
                      <p className="text-white text-xs font-heading">{t.name?.toUpperCase()}</p>
                      <p className="text-zinc-600 text-[10px]">{t.role}{t.location ? ` \u2022 ${t.location}` : ''}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats bar */}
          <div className="grid grid-cols-3 gap-4 mt-8">
            {[
              { value: '50+', label: 'Events Filmed' },
              { value: '200+', label: 'Athletes Captured' },
              { value: '98%', label: 'Client Satisfaction' },
            ].map((s, i) => (
              <div key={s.label} className="text-center py-4 border border-zinc-800 bg-zinc-900/30" data-testid={`stat-${i}`}>
                <p className="text-[#e8ff00] text-2xl md:text-3xl font-heading">{s.value}</p>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Leave a Review */}
          <div className="mt-8 border border-zinc-800 bg-zinc-900/30 p-6" data-testid="leave-review-section">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white font-heading text-sm">WORKED WITH US? LEAVE A REVIEW</p>
              {!showForm && (
                <button onClick={() => setShowForm(true)}
                  className="px-4 py-2 border border-[#e8ff00]/30 text-[#e8ff00] text-xs font-heading uppercase tracking-wider hover:bg-[#e8ff00]/10 transition-colors"
                  data-testid="open-review-form-btn">
                  Write Review
                </button>
              )}
            </div>
            {showForm && <ReviewSubmitForm onSubmitted={fetchReviews} />}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HyroxContentPage() {
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(false);

  // Inject structured data (SEO schemas)
  useEffect(() => {
    const scripts = [];

    // FAQPage schema
    scripts.push(injectJsonLd('hyrox-content-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: HYROX_CONTENT_FAQ.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    }));

    // Service schema
    scripts.push(injectJsonLd('hyrox-content-service-schema', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'HYROX Cinematic Video Production',
      description: 'Professional cinematic video production for HYROX athletes, gyms, and events. Specializing in race recaps, hype reels, athlete profiles, and social media content packages.',
      provider: {
        '@type': 'Organization',
        name: 'NinjAthlete',
        url: 'https://ninjathlete.com',
      },
      serviceType: 'Video Production',
      areaServed: { '@type': 'Country', name: 'United States' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'HYROX Content Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Race Day Recaps', description: 'Cinematic highlights of your HYROX race' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Hype Reels', description: 'High-energy short-form content for social media' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Event Films', description: 'Full-length event coverage for gyms and brands' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Athlete Profiles', description: 'Personal story-driven films showcasing athlete journeys' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Gym & Brand Content', description: 'Monthly content packages for fitness businesses' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Social Media Packages', description: '3-5 videos per session, optimized for all platforms' } },
        ],
      },
    }));

    // LocalBusiness schema
    scripts.push(injectJsonLd('hyrox-content-local-schema', {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'NinjAthlete Content Studio',
      description: 'Professional HYROX and fitness video production studio specializing in cinematic race films, hype reels, and athlete content.',
      url: 'https://ninjathlete.com/hyrox-content',
      image: 'https://ninjathlete.com/posters/hyrox-recap.jpg',
      priceRange: '$$',
      address: { '@type': 'PostalAddress', addressCountry: 'US' },
      areaServed: { '@type': 'Country', name: 'United States' },
      sameAs: [
        'https://www.instagram.com/bdavie_1470/',
        'https://www.youtube.com/channel/UCLjnz4zt_YmTR_E1NkEUIWQ',
        'https://www.instagram.com/winvisual',
      ],
    }));

    // VideoObject schema for portfolio
    scripts.push(injectJsonLd('hyrox-content-video-schema', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'HYROX Cinematic Video Portfolio',
      itemListElement: VIDEOS.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'VideoObject',
          name: v.title,
          description: `${v.category} — Professional HYROX cinematic content by NinjAthlete`,
          thumbnailUrl: `https://ninjathlete.com${v.poster}`,
          contentUrl: `https://ninjathlete.com${v.src}`,
          uploadDate: '2025-01-01',
        },
      })),
    }));

    return () => scripts.forEach(s => s?.parentNode?.removeChild(s));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (ctaDismissed) return;
      const portfolio = document.getElementById('portfolio');
      const inquire = document.getElementById('inquire');
      if (!portfolio) return;
      const portfolioBottom = portfolio.getBoundingClientRect().bottom;
      const inquireTop = inquire ? inquire.getBoundingClientRect().top : Infinity;
      // Show after scrolling past portfolio, hide when inquiry form is in view
      setShowFloatingCTA(portfolioBottom < 0 && inquireTop > window.innerHeight);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ctaDismissed]);

  return (
    <main className="bg-black min-h-screen" data-testid="hyrox-content-page">
      <PageSEO
        title="HYROX Cinematic Content | Race Films, Hype Reels & Athlete Videos | NinjAthlete"
        description="Professional HYROX video production — cinematic race recaps, hype reels, athlete profiles, gym content, and social media packages. Covering HYROX events across the USA. Get a custom quote today."
        path="/hyrox-content"
      />

      {/* ─── Hero with Video Background ─── */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden" data-testid="content-hero">
        <video
          autoPlay muted loop playsInline
          poster={VIDEOS[1].poster}
          className="absolute inset-0 w-full h-full object-cover"
          src={VIDEOS[1].src}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />

        <div className="relative z-10 container-ninja pb-16 pt-32">
          <span className="inline-block px-4 py-1.5 bg-[#e8ff00] text-black text-[10px] font-heading tracking-widest uppercase mb-6">
            NinjAthlete x Content Creation
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-7xl text-white font-heading leading-[0.95] mb-4 max-w-3xl" data-testid="content-hero-title">
            CINEMATIC <span className="text-[#e8ff00]">HYROX</span> CONTENT
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-xl mb-8">
            Professional race films, hype reels, and athlete content that captures the intensity of HYROX.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#inquire" className="px-6 py-3 bg-[#e8ff00] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity inline-flex items-center gap-2">
              Get a Quote <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#portfolio" className="px-6 py-3 border border-white/30 text-white font-heading text-sm uppercase tracking-wider hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              View Portfolio <Play className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── Video Portfolio ─── */}
      <section id="portfolio" className="bg-black section-spacing" data-testid="content-portfolio">
        <div className="container-ninja">
          <div className="max-w-4xl mx-auto mb-10">
            <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3 flex items-center gap-2">
              <Film className="w-4 h-4" /> Portfolio
            </p>
            <h2 className="text-3xl md:text-4xl text-white font-heading mb-3">OUR RECENT WORK</h2>
            <p className="text-zinc-400 max-w-xl">From race day recaps to cinematic hype reels — each video is crafted to capture the raw intensity of HYROX competition.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {VIDEOS.map(v => <VideoCard key={v.id} video={v} />)}
          </div>
        </div>
      </section>

      {/* ─── Featured YouTube Content ─── */}
      <section className="bg-[#050505] section-spacing" data-testid="content-youtube-gallery">
        <div className="container-ninja">
          <div className="max-w-4xl mx-auto mb-10">
            <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3 flex items-center gap-2">
              <Youtube className="w-4 h-4" /> Featured Films
            </p>
            <h2 className="text-3xl md:text-4xl text-white font-heading mb-3">FROM OUR CREATORS</h2>
            <p className="text-zinc-400 max-w-xl">Full-length films and highlight reels from our creative team — showcasing the energy, emotion, and intensity we bring to every project.</p>
          </div>

          {/* Main Feature */}
          <div className="max-w-4xl mx-auto mb-6">
            <div className="relative aspect-video border border-zinc-800 overflow-hidden bg-zinc-900 group" data-testid="yt-featured-main">
              <iframe
                src="https://www.youtube.com/embed/dihzjN-NwVA?rel=0"
                title="NinjAthlete Collab - If You Don't Fail, You're Not Even Trying"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-heading">NINJATHLETE COLLAB</p>
                <p className="text-zinc-500 text-xs">"If you don't fail, you're not even trying"</p>
              </div>
              <span className="text-[#e8ff00] text-[10px] uppercase tracking-wider font-heading px-2 py-1 border border-[#e8ff00]/30">Featured</span>
            </div>
          </div>

          {/* Video Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {[
              { id: 'f2N5ghBgN5E', title: 'Mayweather Boxing Hype Video', creator: 'Brandon Davie', tag: 'Hype Reel' },
              { id: 'GeFUKw2LHyM', title: 'Body Machine Fitness', creator: 'Brandon Davie', tag: 'Gym Content' },
            ].map(yt => (
              <div key={yt.id} className="border border-zinc-800 bg-zinc-900/30 overflow-hidden group hover:border-[#e8ff00]/30 transition-colors" data-testid={`yt-${yt.id}`}>
                <div className="relative aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${yt.id}?rel=0`}
                    title={yt.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
                <div className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-heading">{yt.title}</p>
                    <p className="text-zinc-600 text-xs">by {yt.creator}</p>
                  </div>
                  <span className="text-zinc-500 text-[9px] uppercase tracking-wider font-heading px-2 py-0.5 border border-zinc-700">{yt.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Services ─── */}
      <section className="bg-[#050505] section-spacing" data-testid="content-services">
        <div className="container-ninja">
          <div className="max-w-4xl mx-auto mb-10">
            <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3 flex items-center gap-2">
              <Camera className="w-4 h-4" /> Services
            </p>
            <h2 className="text-3xl md:text-4xl text-white font-heading mb-3">WHAT WE CREATE</h2>
            <p className="text-zinc-400 max-w-xl">Whether you're an athlete looking to document your race, a gym building your brand, or a coach growing your audience — we've got you covered.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="border border-zinc-800 bg-zinc-900/40 p-6 hover:border-[#e8ff00]/30 transition-colors group" data-testid={`service-${i}`}>
                <s.icon className="w-6 h-6 text-[#e8ff00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-heading text-lg mb-2">{s.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-zinc-500 text-sm mb-4">All packages are custom-quoted based on your needs</p>
            <a href="#inquire" className="inline-flex items-center gap-2 px-6 py-3 bg-[#e8ff00] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
              Inquire for Pricing <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── Our Creators ─── */}
      <section className="bg-black section-spacing" data-testid="content-creators">
        <div className="container-ninja">
          <div className="max-w-4xl mx-auto mb-10">
            <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3">Our Creative Team</p>
            <h2 className="text-3xl md:text-4xl text-white font-heading mb-3">THE FILMMAKERS</h2>
            <p className="text-zinc-400 max-w-xl">We partner with top-tier videographers who specialize in fitness and athletic content.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Brandon Davie */}
            <div className="border border-zinc-800 bg-zinc-900/40 p-6 hover:border-[#e8ff00]/30 transition-colors" data-testid="creator-brandon">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-[#e8ff00]/10 border border-[#e8ff00]/30 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6 text-[#e8ff00]" />
                </div>
                <div>
                  <h3 className="text-white font-heading text-xl">BRANDON DAVIE</h3>
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Cinematographer / Director</p>
                </div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                Specializing in high-energy athletic content and race day cinematography. Brandon brings a cinematic eye to every HYROX event, capturing the raw emotion and intensity that defines the sport.
              </p>
              <div className="flex gap-3">
                <a href="https://www.instagram.com/bdavie_1470/" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-[#e8ff00] transition-colors">
                  <Instagram className="w-3.5 h-3.5" /> @bdavie_1470
                </a>
                <a href="https://www.youtube.com/channel/UCLjnz4zt_YmTR_E1NkEUIWQ" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-[#e8ff00] transition-colors">
                  <Youtube className="w-3.5 h-3.5" /> YouTube
                </a>
              </div>
            </div>

            {/* WINVISUAL / Steven Win */}
            <div className="border border-zinc-800 bg-zinc-900/40 p-6 hover:border-[#e8ff00]/30 transition-colors" data-testid="creator-winvisual">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-[#e8ff00]/10 border border-[#e8ff00]/30 flex items-center justify-center flex-shrink-0">
                  <Film className="w-6 h-6 text-[#e8ff00]" />
                </div>
                <div>
                  <h3 className="text-white font-heading text-xl">WINVISUAL</h3>
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Creative Studio / Steven Win</p>
                </div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                A full-service creative studio offering short-form content, brand films, event highlights, and monthly retainer packages. Known for polished, story-driven content that elevates brands.
              </p>
              <div className="flex gap-3">
                <a href="https://www.instagram.com/winvisual" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-[#e8ff00] transition-colors">
                  <Instagram className="w-3.5 h-3.5" /> @winvisual
                </a>
                <a href="https://stvnwin.com/" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-zinc-500 text-xs hover:text-[#e8ff00] transition-colors">
                  <Globe className="w-3.5 h-3.5" /> stvnwin.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Who This Is For ─── */}
      <section className="bg-[#050505] section-spacing">
        <div className="container-ninja">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl text-white font-heading mb-8 text-center">WHO THIS IS FOR</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: 'Athletes', desc: 'Document your race, build your personal brand, and share cinematic content that stands out from phone footage.', color: '#e8ff00' },
                { title: 'Gyms & Coaches', desc: 'Elevate your marketing with professional content that showcases your facility, community, and training programs.', color: '#00F0FF' },
                { title: 'Brands & Sponsors', desc: 'High-quality branded content and event films that capture your brand\'s story within the HYROX ecosystem.', color: '#E63946' },
              ].map((item, i) => (
                <div key={item.title} className="text-center p-6 border border-zinc-800 bg-zinc-900/30">
                  <div className="w-12 h-12 mx-auto mb-4 border-2 flex items-center justify-center" style={{ borderColor: item.color }}>
                    <span className="text-2xl font-heading" style={{ color: item.color }}>{i + 1}</span>
                  </div>
                  <h3 className="text-white font-heading text-lg mb-2">{item.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ Section (SEO Content) ─── */}
      <section className="bg-black section-spacing" data-testid="content-faq">
        <div className="container-ninja">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3">Frequently Asked Questions</p>
              <h2 className="text-3xl md:text-4xl text-white font-heading">HYROX CONTENT FAQ</h2>
            </div>
            <div className="space-y-3">
              {HYROX_CONTENT_FAQ.map((faq, i) => (
                <details key={faq.q} className="group border border-zinc-800 bg-zinc-900/30 hover:border-zinc-700 transition-colors" data-testid={`faq-${i}`}>
                  <summary className="flex items-center justify-between p-5 cursor-pointer text-white text-sm font-medium list-none">
                    <span>{faq.q}</span>
                    <ChevronRight className="w-4 h-4 text-zinc-500 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 text-zinc-400 text-sm leading-relaxed border-t border-zinc-800/50 pt-4">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Testimonials / Social Proof ─── */}
      <TestimonialsSection />

      {/* ─── Inquiry Form ─── */}
      <section id="inquire" className="bg-black section-spacing" data-testid="content-inquiry-section">
        <div className="container-ninja">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3 flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" /> Get Started
              </p>
              <h2 className="text-3xl md:text-4xl text-white font-heading mb-3">BOOK YOUR SHOOT</h2>
              <p className="text-zinc-400 max-w-lg mx-auto">Tell us about your event or project. We'll get back to you within 24-48 hours with a custom quote.</p>
            </div>

            <div className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8">
              <InquiryForm />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Back to HYROX Hub ─── */}
      <section className="bg-[#050505] section-spacing">
        <div className="container-ninja text-center">
          <h2 className="text-2xl text-white font-heading mb-4">Ready to Race?</h2>
          <p className="text-zinc-400 text-sm mb-6 max-w-md mx-auto">Explore our complete HYROX Command Center for training guides, race tools, and the largest gym directory in the USA.</p>
          <Link to="/hyrox" className="inline-flex items-center gap-2 px-6 py-3 bg-[#e8ff00] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity" data-testid="back-to-hyrox-btn">
            HYROX Hub <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ─── Floating Book Now CTA ─── */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-500 ease-out ${
          showFloatingCTA && !ctaDismissed ? 'translate-y-0' : 'translate-y-full'
        }`}
        data-testid="floating-cta"
      >
        <div className="bg-zinc-950/95 backdrop-blur-md border-t border-[#e8ff00]/20">
          <div className="container-ninja py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="hidden sm:flex w-8 h-8 bg-[#e8ff00]/10 border border-[#e8ff00]/30 items-center justify-center flex-shrink-0">
                <Film className="w-4 h-4 text-[#e8ff00]" />
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-heading truncate">Ready to create cinematic HYROX content?</p>
                <p className="text-zinc-500 text-xs hidden sm:block">Custom race films, hype reels & athlete profiles</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href="#inquire"
                onClick={() => setCtaDismissed(true)}
                className="px-5 py-2.5 bg-[#e8ff00] text-black font-heading text-xs uppercase tracking-wider hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"
                data-testid="floating-cta-book-btn"
              >
                Book Now <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <button
                onClick={() => setCtaDismissed(true)}
                className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-zinc-300 transition-colors"
                data-testid="floating-cta-dismiss"
                aria-label="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
